// src/components/DetailSidebar.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ShieldAlert, Share2, Download, Loader2, Save, Fingerprint, Zap, Maximize2, X, Copy, Eye, EyeOff, Sparkles,
  Calendar, Key, Info, Globe, Monitor, MapPin, Activity, HardDrive, Shield, Network, Terminal, Cpu, ShieldCheck, FileWarning, Hash
} from "lucide-react";

import { SlackIcon } from 'lucide-react';
import { SiJira } from "react-icons/si";
import FrostedModal from "./modals/FrostedModal";
import { MODAL_CONFIG, MODAL_TYPE, resolveModalFromApi } from "../utils/errors";
import { apiFetch, ensureAuthOrRedirect, redirectToLogin, attachExpiryTimer, getToken } from "../utils/auth";
import { useNavigate } from "react-router-dom";

// Helper for email validation
const isEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export default function DetailSidebar({ open, row, initialParams, jwtToken, onClose, onUnlocked }) {
  const TABS = ["Summary", "History"];
  const [tab, setTab] = useState("Summary");
  const [checkingAccess, setCheckingAccess] = useState(null); // Stores the username of the active check
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [data, setData] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [slackPushing, setSlackPushing] = useState(false);
  const [slackPushStatus, setSlackPushStatus] = useState(null);
  const [archiveDownloading, setArchiveDownloading] = useState(false);
  const navigate = useNavigate();

  const [modal, setModal] = useState({ open: false, type: null });
  const openModalFor = (type) => setModal({ open: true, type });
  const closeModal = () => {
    setModal({ open: false, type: null });
    setSaveStatus(null);   // clears inline banner
    setError("");          // clears error text in content area
    setTab("Summary");
  };
  const cfg = modal.type ? MODAL_CONFIG[modal.type] : null;

  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingStatus, setDownloadingStatus] = useState(null);

  useEffect(() => {
    if (!open) {
      setTab("Summary");
      setSaveStatus(null);
      setError("");
      setSaving(false);
      setDownloading(false);
    }
  }, [open]);

  const abortRef = useRef(null);
  const canCall = open && initialParams && jwtToken;

  const DETAIL_URL =
    "https://whiteintel.io/api/regular/app/v2/globalsearch/get_incident_detail_v3.php";
  const UNLOCK_URL =
    "https://whiteintel.io/api/regular/app/v2/incidents/get_unlock_item_handler.php";

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Initial fetch on open
  useEffect(() => {
    if (!canCall) return;

    setLoading(true);
    setError("");
    setData(null);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      try {
        const res = await fetch(DETAIL_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(initialParams),
          signal: controller.signal,
        });

        if ([401, 403, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setData(json);

      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Failed to load details");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    run();

    return () => controller.abort();
  }, [open, initialParams, jwtToken, canCall]);

  const isLocked = data && data.is_unlocked === false;
  const systemInfos = data?.data?.systeminfo || [];

  const metaFirstSeen = systemInfos.length > 0 ? systemInfos[systemInfos.length - 1].log_date : "—";
  const metaLastSeen  = systemInfos.length > 0 ? systemInfos[0].log_date : "—";

const getArchiveState = (info) => {
  const status = info?.archive_status;
  if (status === "uploaded") {
    return { tone: "available", label: "Available", downloadable: true };
  }
  if (status === "deduplicated") {
    return { tone: "dedup", label: "Deduplicated", downloadable: true };
  }
  if (status === "pending") {
    return { tone: "pending", label: "Processing", downloadable: false };
  }
  if (status === "failed") {
    return { tone: "failed", label: "Upload failed", downloadable: false };
  }
  return { tone: "none", label: "No archive", downloadable: false };
};

  const historyHasMissingMetadata = useMemo(() => {
  if (!systemInfos || systemInfos.length <= 1) return false;

  const latest = systemInfos[0];
  const historical = systemInfos.slice(1);

  // The fields you identified as important
  const criticalFields = ["hostname", "ip", "country", "os", "anti_viruses", "malware_path"];

  return criticalFields.some((field) => {
    const isMissingInLatest = !latest[field] || String(latest[field]).trim() === "" || latest[field] === "N/A";
    const existsInHistory = historical.some(
      (h) => h[field] && String(h[field]).trim() !== "" && h[field] !== "N/A"
    );
    return isMissingInLatest && existsInHistory;
  });
}, [systemInfos]);

  const handlePushToSlack = async () => {
    setSlackPushing(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/slack/send_slack.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: Number(initialParams.id),
            query: String(initialParams.query || ""),
            type: String(initialParams.type || ""),
            detail_type: String(initialParams.detail_type || ""),
          }),
        }
      );

      let result = {};
      try { result = await res.json(); } catch (e) { result = {}; }

      if (!res.ok || result.status !== "success") {
        setPushStatus({
          type: "error",
          message: result?.message || result?.error || `Failed to push to Slack.`,
        });
        return;
      }

      setSlackPushStatus({
        type: "success",
        message: result.message || "Pushed to Slack successfully",
      });
    } catch (err) {
      setPushStatus({ type: "error", message: "Unexpected error while pushing to Slack." });
    } finally {
      setSlackPushing(false);
    }
  };

  const handlePushToJira = async () => {
    setPushing(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/jira/create_jira_ticket.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: Number(initialParams.id),
            query: String(initialParams.query || ""),
            type: String(initialParams.type || ""),
            detail_type: String(initialParams.detail_type || ""),
          }),
        }
      );

      let result = {};
      try { result = await res.json(); } catch (e) { result = {}; }

      if (!res.ok || result.status !== "success") {
        setPushStatus({
          type: "error",
          message: result?.message || result?.error || `Failed to push to Jira.`,
        });
        return;
      }

      setPushStatus({
        type: "success",
        message: (
          <>
            {result.message || "Pushed to Jira successfully"}{" "}
            {result.jira_ticket && result.ticket_url && (
              <>
                (<a href={result.ticket_url} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">{result.jira_ticket}</a>)
              </>
            )}
          </>
        ),
      });

    } catch (err) {
      setPushStatus({ type: "error", message: "Unexpected error while pushing to Jira." });
    } finally {
      setPushing(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    setSaveStatus(null);

    try {
      const response = await fetch(
        "https://whiteintel.io/api/regular/app/v2/incidents/save_incident.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: Number(initialParams.id),
            query: String(initialParams.query || ""),
            detail: String(initialParams.type || ""),
            breach_type: String(initialParams.detail_type),
          }),
        }
      );

      if ([401, 419, 440].includes(response.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${response.status}`);
      }

      const result = await response.json();

      if (response.status === 403 && result.message?.includes("Forbidden search")) {
        openModalFor("FORBIDDEN_SEARCH");
        return;
      }

      if (result.is_saved) {
        setSaveStatus({ type: "success", message: "Incident saved successfully." });
      } else {
        setSaveStatus({ type: "error", message: result.error || "Incident was not saved." });
      }
    } catch (err) {
      setSaveStatus({ type: "error", message: "Something went wrong while saving the incident." });
    } finally {
      setSaving(false);
    }
  };


const handleNeuralAnalysis = async (username) => {
  if (checkingAccess) return; // Prevent concurrent clicks
  
  setCheckingAccess(username);
  try {
    const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/check_access.php", {
      headers: { 
        Authorization: `Bearer ${localStorage.getItem("token")}` 
      }
    });
    const json = await res.json();

    if (json.success && json.subscription === 'tifirm') {
      window.open(`/investigations?query=${encodeURIComponent(username)}`, '_blank');
    } else {
      setShowUpgradeModal(true);
    }
  } catch (err) {
    console.error("Access check failed", err);
    setShowUpgradeModal(true);
  } finally {
    setCheckingAccess(null); // Reset loading state
  }
};


const [archiveDownloadingId, setArchiveDownloadingId] = useState(null);
const [pendingDownloadId, setPendingDownloadId] = useState(null);

useEffect(() => {
  if (!pendingDownloadId) return;
  const onKey = (e) => {
    if (e.key === "Escape") setPendingDownloadId(null);
  };
  // Close when clicking anywhere outside the popover (excluding the trigger).
  const onDocMouseDown = (e) => {
    if (e.target.closest && e.target.closest("[data-archive-warning]")) return;
    setPendingDownloadId(null);
  };
  document.addEventListener("keydown", onKey);
  document.addEventListener("mousedown", onDocMouseDown);
  return () => {
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("mousedown", onDocMouseDown);
  };
}, [pendingDownloadId]);

const handleDownloadArchive = async (systeminfoId) => {
  if (!systeminfoId || archiveDownloadingId) return;

  setArchiveDownloadingId(systeminfoId);
  setSaveStatus(null);

  try {
    const res = await fetch(
      `https://whiteintel.io/api/regular/app/v2/globalsearch/download_archive.php?systeminfo_id=${encodeURIComponent(systeminfoId)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      throw new Error(`AUTH_REDIRECT_${res.status}`);
    }

    let payload = {};
    try { payload = await res.json(); } catch { payload = {}; }

    if (res.status === 403) {
      setSaveStatus({
        type: "error",
        message: payload?.error || "Full log archive downloads require the Threat Intelligence subscription.",
        cta: { label: "Upgrade", href: "/upgrades" },
      });
      return;
    }

    if (res.status === 429) {
      const retry = payload?.retry_after_seconds;
      setSaveStatus({
        type: "error",
        message: retry
          ? `Rate limit hit. Try again in ${Math.ceil(retry / 60)} minute(s).`
          : (payload?.error || "Too many downloads. Please slow down."),
      });
      return;
    }

    if (res.status === 404) {
      setSaveStatus({ type: "error", message: "No archive available for this log." });
      return;
    }

    if (res.status === 409) {
      setSaveStatus({
        type: "error",
        message: "Archive is still being prepared. Try again in a few minutes.",
      });
      return;
    }

    if (!res.ok || !payload?.success || !payload?.url) {
      setSaveStatus({
        type: "error",
        message: payload?.error || "Could not generate download link.",
      });
      return;
    }

    const a = document.createElement("a");
    a.href = payload.url;
    a.download = payload.filename || `log_${systeminfoId}.tar.zst`;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();

    const sizeMb = payload.size_bytes
      ? ` (${(payload.size_bytes / 1024 / 1024).toFixed(1)} MB)`
      : "";
    setSaveStatus({
      type: "success",
      message: `Archive download started.`,
    });
  } catch (err) {
    if (!String(err?.message || "").startsWith("AUTH_REDIRECT_")) {
      setSaveStatus({
        type: "error",
        message: "Unexpected error while preparing archive download.",
      });
    }
  } finally {
    setArchiveDownloadingId(null);
  }
};
  const downloadBreachReport = async (breachId, query, detailType) => {
    if (downloading) return;
    setDownloading(true);
    setSaveStatus(null);

    try {
      const response = await fetch(
        "https://whiteintel.io/api/regular/app/v2/globalsearch/get_download_as_txt_handler.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            id: Number(initialParams.id),
            query: String(initialParams.query || ""),
            type: String(initialParams.type || ""),
            detail_type: String(initialParams.detail_type),
          }),
        }
      );

      if ([401, 419, 440].includes(response.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${response.status}`);
      }

      if (!response.ok) {
        let payload;
        try { payload = await response.json(); } catch { payload = { message: await response.text() }; }
        if (response.status === 403 && /forbidden search/i.test(payload?.message || "")) {
          setSaveStatus({ type: "error", message: "Download unavailable under your current plan." });
          return;
        }
        setSaveStatus({ type: "error", message: fmtApiError(payload, "Download failed.") });
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeQuery = String(query || "").replace(/[^a-zA-Z0-9.-]/g, "_");
      a.href = url;
      a.download = `${breachId}_${safeQuery}_${detailType}_breach_details.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSaveStatus({ type: "success", message: "Report downloaded." });
    } catch (error) {
      setSaveStatus({ type: "error", message: `Download failed — ${error?.message || "Please try again."}` });
    } finally {
      setDownloading(false);
    }
  };

  const handleUnlock = async () => {
    if (!initialParams?.id) return;
    setUnlocking(true);
    setError("");
    const buildBody = () =>
      JSON.stringify({
        id: Number(initialParams.id),
        query: String(initialParams.query || ""),
        detail: String(initialParams.type || ""),
        ...(initialParams.detail_type === "emailAddress" && {
          search_type: String(initialParams.detail_type),
        }),
      });

    try {
      const res = await fetch(UNLOCK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: buildBody(),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const json = await res.json();
      const msg = String(json?.message || "").toLowerCase();

      if (json?.status === "error" || json?.success === false) {
        if (json?.is_unlocked === false && /insufficient.*unlock/i.test(msg)) {
          openModalFor(MODAL_TYPE.INSUFFICIENT_UNLOCK);
        } else {
          openModalFor(MODAL_TYPE.GENERIC_ERROR);
        }
        return;
      }

      const isSuccess = json?.success === true || String(json?.status || "").toLowerCase() === "success";

      if (isSuccess && json?.is_unlocked === true) {
        setError("");
        setData((prev) => (prev ? { ...prev, is_unlocked: true } : prev));
        setLoading(true);
        const refetch = await fetch(DETAIL_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(initialParams),
        });

        if (!refetch.ok) throw new Error(`Refetch HTTP ${refetch.status}`);
        const fresh = await refetch.json();
        setData(fresh);
        try { onUnlocked?.(); } catch { }
      } else {
        setError(json?.message || "Unlock failed");
        return;
      }
    } catch (e) {
      setError(e.message || "Unlock failed");
    } finally {
      setUnlocking(false);
      setLoading(false);
    }
  };

 const compromisedFields = useMemo(() => {
  if (initialParams?.type !== "database" || !data?.data?.data_fields) return [];
  return data.data.data_fields.split(",").map(f => f.trim());
}, [data, initialParams]);

  const meta = useMemo(() => {
    const m = data?.meta || data || {};
    return {
      logId: String(m.log_id || row?.log_id || initialParams?.id || "—"),
      credentials: Array.isArray(m.credentials) ? m.credentials : (Array.isArray(data?.credentials) ? data.credentials : []),
    };
  }, [data, row, initialParams]);

  const Loader = () => (
    <div className="p-5 space-y-5">
      <div className="h-5 w-36 bg-neutral-800/60 animate-pulse rounded" />
      <div className="space-y-3">
        <div className="h-4 w-64 bg-neutral-800/60 animate-pulse rounded" />
        <div className="h-4 w-48 bg-neutral-800/60 animate-pulse rounded" />
      </div>
      <div className="space-y-2">
        <div className="h-4 w-full bg-neutral-800/60 animate-pulse rounded" />
        <div className="h-4 w-5/6 bg-neutral-800/60 animate-pulse rounded" />
      </div>
    </div>
  );

  return (
    <div className={`fixed inset-0 z-[80] ${open ? "" : "pointer-events-none"}`} role="dialog" aria-modal="true">
      <div className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
<aside className={`absolute right-0 top-0 h-full w-full max-w-3xl border-l border-neutral-800 bg-[#000414] shadow-[0_0_15px_rgba(56,189,248,0.05)] transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>        <div className="flex items-start justify-between px-5 py-4 border-b border-neutral-800">
          <div>
            <div className="flex items-center gap-2 text-sm text-neutral-400"><ShieldAlert className="h-4 w-4" /><span>Incident Detail</span></div>
            <h2 className="mt-1 text-xl font-semibold">Incident Detail - {meta.logId}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button title="Push to Jira" onClick={handlePushToJira} disabled={pushing} className="flex h-6 w-6 items-center justify-center text-gray-300 hover:text-gray-400 disabled:opacity-50">
              {pushing ? <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg> : <SiJira className="h-4 w-4" />}
            </button>
            <button title="Push to Slack" onClick={handlePushToSlack} disabled={slackPushing} className="flex h-6 w-6 items-center justify-center text-gray-300 hover:text-gray-400 disabled:opacity-50">
              {slackPushing ? <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg> : <SlackIcon className="h-4 w-4" />}
            </button>
            <IconBtn title="Save" onClick={handleSave}>{saving ? <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25"/><path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" className="opacity-75"/></svg> : <Save className="h-4 w-4" />}</IconBtn>
           

            <IconBtn title="Download" onClick={() => downloadBreachReport(Number(initialParams.id), String(initialParams.query || ""), String(initialParams.type || ""))} disabled={downloading}>{downloading ? <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/><path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" className="opacity-75"/></svg> : <Download className="h-4 w-4" />}</IconBtn>
            <IconBtn title="Close" onClick={onClose}><X className="h-4 w-4" /></IconBtn>
          </div>
        </div>

        {(saveStatus || pushStatus || slackPushStatus) && (
          <div className={`mx-5 mt-3 flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${(saveStatus?.type === "success" || pushStatus?.type === "success" || slackPushStatus?.type === "success") ? "border-green-500/30 bg-green-500/10 text-green-200" : "border-red-500/30 bg-red-500/10 text-red-200"}`}>
            <span className="min-w-0">{saveStatus?.message || pushStatus?.message || slackPushStatus?.message}</span>
            <div className="flex items-center gap-2 shrink-0">
              {saveStatus?.cta && (
                <a
                  href={saveStatus.cta.href}
                  className="rounded-md border border-sky-500/50 bg-sky-500/15 px-2.5 py-1 text-xs font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/70 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                >
                  {saveStatus.cta.label}
                </a>
              )}
              <button onClick={() => { setSaveStatus(null); setPushStatus(null); setSlackPushStatus(null); }} className="text-xs text-neutral-400 hover:text-white">Dismiss</button>
            </div>
          </div>
        )}

<div className="px-5">
  <nav className="flex items-center gap-6 text-sm">
    {/* Only show tabs if it's a stealer. Otherwise, don't render the nav at all. */}
    {initialParams?.type === "stealer" && TABS.map((t) => {
      const active = tab === t;
      return (
        <button 
          key={t} 
          onClick={() => setTab(t)} 
          className={`py-3 transition-colors ${active ? "text-white" : "text-neutral-400 hover:text-neutral-200"}`}
        >
          {t}
          {t === "History" && systemInfos.length > 1 && (
            <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-bold text-sky-300 border border-sky-500/30">
              {systemInfos.length}
            </span>
          )}
          {t === "History" && systemInfos.some((info) => getArchiveState(info).downloadable) && (
            <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-300">
              <Download className="h-2.5 w-2.5" />
              Full Download Available
            </span>
          )}
          <div className={`mt-2 h-[2px] rounded-full transition-all ${active ? "bg-white/70" : "bg-transparent"}`} />
        </button>
      );
    })}
  </nav>
</div>

        {showUpgradeModal && (
  <div 
    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-[6px] animate-in fade-in duration-300"
    onClick={() => setShowUpgradeModal(false)}
  >
    <div 
      className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#000414] shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden animate-in zoom-in-95 duration-300"
      onClick={(e) => e.stopPropagation()}
    >
      <button 
        onClick={() => setShowUpgradeModal(false)}
        className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/20 text-neutral-500 hover:text-white hover:bg-white/10 transition-all border border-white/5"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="h-24 bg-gradient-to-br from-sky-500/20 to-transparent flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-sky-500/10 to-transparent h-full w-full animate-[scan_4s_linear_infinite] opacity-30" />
        <div className="relative flex items-center gap-4 px-8 w-full">
           <div className="w-12 h-12 rounded-xl bg-[#000414] border border-sky-400/30 flex items-center justify-center shadow-lg">
             <Sparkles className="w-6 h-6 text-sky-400 animate-pulse" />
           </div>
           <div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />
                <span className="text-[10px] font-black text-sky-400 uppercase tracking-[0.3em]">Whiteintel AI Assistant</span>
              </div>
              <h3 className="text-xl font-black text-white uppercase tracking-tight">Forensic Identity Attribution</h3>
           </div>
        </div>
      </div>

      <div className="px-8 py-6">
        <div className="mb-6">
          <p className="text-sm text-neutral-400 leading-relaxed">
            Leverage autonomous heuristics to de-anonymize targets and map lateral exposure. 
            <span className="block mt-1 text-sky-400 font-semibold italic text-xs">
              Threat Intel license is required for Whiteintel AI Assistant.
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {[
            { icon: <Fingerprint className="w-4 h-4 text-sky-400" />, title: "Identity Correlation", desc: "Resolve real names, phone numbers & Govt IDs." },
            { icon: <Zap className="w-4 h-4 text-amber-400" />, title: "Neural Scoring", desc: "Prioritize critical assets via AI prioritization." },
            { icon: <ShieldAlert className="w-4 h-4 text-emerald-400" />, title: "Footprinting", desc: "Trace historical identity exposure across logs." }
          ].map((feat, i) => (
            <div key={i} className="flex flex-col gap-2 p-3.5 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors group">
              <div className="flex items-center gap-2">
                {feat.icon}
                <p className="text-[10px] font-black text-sky-100 uppercase tracking-widest">{feat.title}</p>
              </div>
              <p className="text-[11px] text-neutral-500 leading-snug">{feat.desc}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row items-center gap-4">
          <button 
            onClick={() => window.location.href = '/settings/billing'}
            className="w-full md:flex-1 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-black text-xs tracking-[0.2em] transition-all shadow-xl shadow-sky-500/30 uppercase active:scale-[0.98]"
          >
            Upgrade to Threat Intel
          </button>
          <button 
            onClick={() => setShowUpgradeModal(false)}
            className="w-full md:w-auto px-6 py-2 text-neutral-600 hover:text-neutral-400 text-[10px] font-black uppercase tracking-[0.3em] transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
)}

<div className="overflow-y-auto p-5 pb-24 h-[calc(100%-100px)] relative">
  {loading ? (
    <Loader />
  ) : error ? (
    <div className="p-5 text-red-400 text-sm">Error: {error}</div>
  ) : (
    <>
      <div className={isLocked ? "blur-sm select-none" : ""}>
        {tab === "Summary" && (
          <div className="space-y-8">
            {/* --- DATABASE LEAK VIEW (HIBP STYLE) --- */}
            {initialParams?.type === "database" ? (
        <>
    <Section title="Breach Overview">
      <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-indigo-400/30 bg-[#000414] shadow-lg">
            <Shield className="h-6 w-6 text-indigo-400" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-white">
              {data?.data?.database_name || "Unknown Breach"}
            </h3>
            <div className="flex items-center gap-2 text-xs text-indigo-300/70">
              <Calendar className="h-3.5 w-3.5" />
              <span>Breach Date: {data?.data?.breach_date || "—"}</span>
            </div>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-neutral-300">
          {data?.data?.breach_description ||
            "This identity was found in a publicly circulated database leak. Database leaks typically contain structured information harvested from compromised web services."}
        </p>
      </div>
    </Section>

    <Section title="Compromised Data">
      <div className="flex flex-wrap gap-2">
        {data?.data?.data_fields ? (
          data.data.data_fields.split(",").map((field, i) => (
            <span key={i} className="flex items-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-800/40 px-3 py-1.5 text-xs font-medium text-neutral-200">
              <div className="h-1 w-1 rounded-full bg-indigo-400" />
              {field.trim().replace(/_/g, " ")}
            </span>
          ))
        ) : (
          <span className="text-sm text-neutral-500 italic">No field data recorded.</span>
        )}
      </div>
    </Section>

    <Section title={`Exposed Accounts (${data?.data?.total || 0})`}>
  <div className="max-h-[400px] overflow-y-auto pr-1 space-y-3 scrollbar-thin scrollbar-thumb-neutral-700 scrollbar-track-transparent">

        {data?.data?.accounts?.length ? (
          data.data.accounts.map((account, idx) => (
            <div key={idx} className="p-3 rounded-lg bg-neutral-900/40 border border-neutral-800 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm text-neutral-400">Email:</div>
                <div className="flex items-center gap-2">
                  <div className="text-neutral-100 font-medium truncate">{account.email || "—"}</div>
                  {account.email && <CopyBtn text={account.email} />}
                </div>
              </div>

              {account.password && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-2">
                  <div className="text-sm text-neutral-400">Password:</div>
                  <div className="flex items-center gap-2">
                    <PasswordField password={account.password} />
                    <CopyBtn text={account.password} />
                  </div>
                </div>
              )}

              {account.hashed_password && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-2">
                  <div className="text-sm text-neutral-400">Hash:</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-indigo-300/80 bg-indigo-500/10 px-2 py-1 rounded truncate max-w-[200px]" title={account.hashed_password}>
                      {account.hashed_password}
                    </span>
                    <CopyBtn text={account.hashed_password} />
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-sm text-neutral-500 italic">No account records available.</div>
        )}
      </div>
    </Section>

    <Section title="Remediations">
      <div className="space-y-3">
        <div className="flex gap-3 items-start">
          <div className="mt-1 h-5 w-5 rounded bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-[10px] font-bold">1</div>
          <p className="text-sm text-neutral-300">Change your password immediately for this service and any other accounts using the same credentials.</p>
        </div>
        <div className="flex gap-3 items-start">
          <div className="mt-1 h-5 w-5 rounded bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-[10px] font-bold">2</div>
          <p className="text-sm text-neutral-300">Enable Multi-Factor Authentication (MFA/2FA) on all sensitive professional and personal accounts.</p>
        </div>
        <div className="flex gap-3 items-start">
          <div className="mt-1 h-5 w-5 rounded bg-indigo-500/20 text-indigo-400 flex items-center justify-center text-[10px] font-bold">3</div>
          <p className="text-sm text-neutral-300">Be alert for targeted phishing attempts using the personal details leaked in this breach.</p>
        </div>
      </div>
    </Section>
  </>
            ) : (
              /* --- STEALER / COMBOLIST VIEW --- */
              <>
                <Section title="What happened">
                  <p className="text-sm text-neutral-300 leading-relaxed">
                    {initialParams?.type === "combolist"
                      ? `Through our dark web monitoring operations, we identified that the user’s credentials were found in leaked credential lists. The most recent occurrence was observed on ${row.last_seen || data?.data?.credentials?.[0]?.log_date || "—"}.`
                      : `Through our dark web monitoring operations, we identified that the following device and credentials have been compromised by information stealers. The most recent occurrence was observed on ${data?.data?.systeminfo?.[0]?.log_date || "—"}.`}
                    {data?.data?.systeminfo?.length > 1 && (
                      <span className="block mt-2">{`Devices of this user has been detected as compromised ${data.data.systeminfo.length} times. ${data.data.systeminfo.length - 1} previous records available in History.`}</span>
                    )}
                  </p>
                </Section>

                <Section title="Meta Data">
                  {historyHasMissingMetadata && (
                    <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-[12px] leading-relaxed text-amber-200/80">
                      <p>
                        Metadata incomplete. 
                        <button onClick={() => setTab("History")} className="ml-1 font-bold text-amber-500 underline hover:text-amber-400">
                          Check History
                        </button>
                        {" "}for additional metadata records.
                      </p>
                    </div>
                  )}

                  {data?.data?.systeminfo?.length ? (
                    <div className="overflow-hidden rounded-lg border border-neutral-800 bg-[#0f172a]/40 shadow-sm">
                      <div className="flex flex-col border-b border-neutral-800 md:flex-row">
                        <div className="flex-1 border-b border-neutral-800 p-4 md:border-b-0 md:border-r">
                          <EnterpriseField icon={<Calendar className="h-3.5 w-3.5" />} label="First Seen" value={metaFirstSeen} highlight />
                        </div>
                        <div className="flex-1 p-4">
                          <EnterpriseField icon={<Activity className="h-3.5 w-3.5" />} label="Last Seen" value={metaLastSeen} highlight />
                        </div>
                      </div>
                      <div className="bg-white/[0.01] p-5">
                        <Grid>
                          {Object.entries(data.data.systeminfo[0]).map(([key, value]) => {
                            if (key === "log_date") return null;
                            const lowerKey = key.toLowerCase();
                            let icon = <Hash className="h-3.5 w-3.5" />;
                            if (lowerKey.includes("ip")) icon = <Network className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("country")) icon = <Globe className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("host") || lowerKey.includes("computer")) icon = <Terminal className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("os") || lowerKey.includes("windows")) icon = <Cpu className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("virus") || lowerKey.includes("defender")) icon = <ShieldCheck className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("path") || lowerKey.includes("file")) icon = <FileWarning className="h-3.5 w-3.5" />;
                            else if (lowerKey.includes("id") || lowerKey.includes("guid")) icon = <Fingerprint className="h-3.5 w-3.5" />;

                            return <EnterpriseField key={key} icon={icon} label={key.replace(/_/g, " ")} value={value} />;
                          })}
                        </Grid>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-20 items-center justify-center rounded-lg border border-neutral-800 border-dashed text-sm text-neutral-500">
                      No system metadata available.
                    </div>
                  )}
                </Section>

                <Section title="Compromised Credentials">
                  <div className="space-y-4">
                    {data?.data?.credentials?.length ? (
                      data.data.credentials.map((cred, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-neutral-900/40 border border-neutral-800">
                          <div className="mt-2 flex items-center gap-2 min-w-0">
                            <div className="text-sm text-neutral-400 shrink-0">Application:</div>
                            <div className="text-neutral-100 font-medium flex-1 min-w-0 truncate" title={cred.url ?? ""}>{cred.url || "—"}</div>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <div className="text-sm text-neutral-400">Username:</div>
                            <div className="text-neutral-100 truncate">{cred.username || "—"}</div>
                            {cred.username && <CopyBtn text={cred.username} />}
                          </div>
                          
                          {/* DYNAMIC FIELD FOR STEALER TYPE */}
                          {cred.password ? (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="text-sm text-neutral-400">Password:</div>
                              <PasswordField password={cred.password || ""} />
                              <CopyBtn text={cred.password} />
                            </div>
                          ) : cred.hashed_password ? (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="text-sm text-neutral-400">Hash:</div>
                              <span className="text-xs font-mono text-neutral-500 truncate max-w-[150px]">{cred.hashed_password}</span>
                              <CopyBtn text={cred.hashed_password} />
                            </div>
                          ) : null}
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-neutral-400">No credentials available.</div>
                    )}
                  </div>
                </Section>

                <Section title="Remediations">
                  <p className="text-sm text-neutral-300 leading-relaxed">Disconnect infected device, run malware scan, reset passwords, enable MFA, and report to IT security.</p>
                </Section>
              </>
            )}
          </div>
        )}

        {/* TAB VISIBILITY LOGIC */}
{tab === "History" && initialParams?.type !== "database" && (
  <div className="space-y-6">
    {data?.data?.systeminfo?.length ? (
      <>
        {/* Summary line */}
        {(() => {
          const total = data.data.systeminfo.length;
          const withArchive = data.data.systeminfo.filter(
            (i) => i.archive_status === "uploaded" || i.archive_status === "deduplicated"
          ).length;
          if (total <= 1) return null;
          return (
            <div className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-3 text-xs text-neutral-400">
              <HardDrive className="mr-1.5 inline h-3.5 w-3.5 text-neutral-500" />
              {withArchive} of {total} incidents have a downloadable full-log archive.
            </div>
          );
        })()}

        <ul className="relative ml-2 pl-6 border-l border-neutral-800 space-y-6">
          {data.data.systeminfo.map((info, idx) => {
            const archive = getArchiveState(info);
            const isDownloading = archiveDownloadingId === info.systeminfo_id;


            return (
              <li key={info.systeminfo_id ?? idx} className="relative">

                <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-sky-500 shadow ring-4 ring-[#000414]" />
                <div className="rounded-lg bg-neutral-900/40 border border-neutral-800 p-4">
                  <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-sky-400">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{info.log_date || "Unknown Date"}</span>
                    {idx === 0 && (
                      <span className="ml-1 rounded-full bg-sky-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-300">
                        Latest
                      </span>
                    )}

                    {/* Archive status + download button */}
                    <div className="ml-auto flex items-center gap-2">
                      <ArchiveBadge state={archive} />

                      {archive.downloadable && info.systeminfo_id && (
                        <div className="relative" data-archive-warning>
                          <button
                            onClick={() => setPendingDownloadId(info.systeminfo_id)}
                            disabled={isDownloading || archiveDownloadingId !== null}
                            title="Download this snapshot's archive"
                            className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/50 bg-sky-500/15 px-2.5 py-1 text-xs font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
                          >
                            {isDownloading ? (
                              <>
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Preparing…
                              </>
                            ) : (
                              <>
                                <Download className="h-3.5 w-3.5" />
                                Download Full Log
                              </>
                            )}
                          </button>
                          {pendingDownloadId === info.systeminfo_id && (
                            <div
                              data-archive-warning
                              className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl border border-amber-500/40 bg-[#000414] shadow-2xl text-left"
                            >
                              <div className="px-4 pt-3.5">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <ShieldAlert className="h-4 w-4 text-amber-300 shrink-0" />
                                  <h3 className="text-sm font-semibold text-white">Heads up</h3>
                                </div>
                                <p className="text-xs text-neutral-300 leading-relaxed">
                                  Infostealer archives may contain malware. Investigate in a sandboxed environment — preferably a Linux VM.
                                </p>
                              </div>
                              <div className="px-3 py-2.5 mt-2 border-t border-white/5 flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setPendingDownloadId(null)}
                                  className="rounded-md border border-neutral-700 bg-neutral-900/40 px-2.5 py-1 text-xs text-neutral-200 hover:bg-neutral-900 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const id = pendingDownloadId;
                                    setPendingDownloadId(null);
                                    handleDownloadArchive(id);
                                  }}
                                  className="rounded-md border border-amber-500/40 bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/25 transition-colors"
                                >
                                  Download
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <Grid>
                    {Object.entries(info).map(([key, value]) => {
                      // Skip metadata fields we display separately
if (["log_date", "systeminfo_id", "archive_status", "archive_size_bytes"].includes(key)) return null;

                      const lowerKey = key.toLowerCase();
                      let icon = <Hash className="h-3.5 w-3.5" />;
                      if (lowerKey.includes("ip")) icon = <Network className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("country")) icon = <Globe className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("host") || lowerKey.includes("computer")) icon = <Terminal className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("os") || lowerKey.includes("windows")) icon = <Cpu className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("virus") || lowerKey.includes("defender")) icon = <ShieldCheck className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("path") || lowerKey.includes("file")) icon = <FileWarning className="h-3.5 w-3.5" />;
                      else if (lowerKey.includes("id") || lowerKey.includes("guid")) icon = <Fingerprint className="h-3.5 w-3.5" />;
                      return <EnterpriseField key={key} icon={icon} label={key.replace(/_/g, " ")} value={value} />;
                    })}
                  </Grid>
                </div>
              </li>
            );
          })}
        </ul>
      </>
    ) : (
      <div className="text-sm text-neutral-400">No historical records available.</div>
    )}
  </div>
)}
      </div>

{isLocked && (
  <div className="absolute inset-x-0 top-[20%] z-20 flex justify-center px-6">
    <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-900/60 p-8 text-center backdrop-blur-xl shadow-2xl">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
        <Shield className="h-7 w-7" />
      </div>
      
{initialParams?.type === "database" ? (
    <>
  <h3 className="text-xl font-black text-white mb-2 uppercase tracking-tighter">Active Subscription Only</h3>
  <p className="text-xs text-neutral-500 mb-6 leading-relaxed px-4">
    Database breach forensics are restricted to active subscriptions. Individual unlocks are not available for this data type.
  </p>

  {/* --- INTELLIGENT DATA PREVIEW --- */}
  <div className="mb-8 rounded-xl border border-white/5 bg-white/[0.02] p-4 text-left shadow-inner">
{(data?.data?.accounts?.[0]?.password || data?.data?.accounts?.[0]?.hashed_password) ? (
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex shrink-0 rounded-full bg-emerald-500/10 p-1.5 border border-emerald-500/20">
          <Key className="h-3.5 w-3.5 text-emerald-400" />
        </div>
        <div>
          <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">Credentials Available</p>
          <p className="text-[11px] text-neutral-400 leading-relaxed">
            Upgrading your license will reveal the compromised password or security hash associated with this identity.
          </p>
        </div>
      </div>
    ) : (
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex shrink-0 rounded-full bg-neutral-500/10 p-1.5 border border-neutral-500/20">
          <Info className="h-3.5 w-3.5 text-neutral-400" />
        </div>
        <div>
          <p className="text-[10px] font-black text-neutral-300 uppercase tracking-widest mb-1">Data Fidelity Note</p>
          <p className="text-[11px] text-neutral-500 leading-relaxed">
            This specific record only contains identity data and PII. No passwords or hashes were recovered in this breach.
          </p>
        </div>
      </div>
    )}
  </div>

  <button 
    onClick={() => navigate('/upgrades')}
    className="w-full rounded-xl bg-indigo-600 py-4 text-[10px] font-black uppercase tracking-[0.2em] text-white hover:bg-indigo-500 transition-all shadow-[0_0_20px_rgba(79,70,229,0.3)]"
  >
    Upgrade your License
  </button>
</>
      ) : (
        <>
          <h3 className="text-lg font-bold text-white mb-2 uppercase tracking-tight">Entry Locked</h3>
          <p className="text-sm text-neutral-400 mb-6 leading-relaxed">Unlock to view full details.</p>
          <button 
            onClick={handleUnlock} 
            disabled={unlocking} 
            className="w-full rounded-xl bg-sky-600 px-6 py-3 text-sm font-bold text-white hover:bg-sky-500 transition-all"
          >
            {unlocking ? "Processing..." : "Unlock Entry"}
          </button>
        </>
      )}
    </div>
  </div>
)}
    </>
  )}
</div>
      </aside>
      {cfg && (
        <FrostedModal open={modal.open} onClose={closeModal} icon={cfg.icon} title={cfg.title} description={cfg.description}
          primaryAction={cfg.primary && { label: cfg.primary.label, onClick: () => { if (cfg.primary.href) window.location.href = cfg.primary.href; else closeModal(); } }}
          secondaryAction={cfg.secondary && { label: cfg.secondary.label, onClick: closeModal }}
        />
      )}

    </div>
  );
}

function IconBtn({ children, title, onClick, disabled }) {
  return (<button className={`p-2 rounded ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5"}`} title={title} onClick={onClick} disabled={disabled}>{children}</button>);
}

const fmtApiError = (res, fallback) => [fallback, res?.message || res?.error || res?.errors?.[0]?.message].filter(Boolean).join(" — ");

function Section({ title, children }) {
  return (<section><h3 className="mb-3 text-sm font-semibold tracking-wide text-neutral-400 uppercase">{title}</h3>{children}</section>);
}

function Grid({ children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
      {children}
    </div>
  );
}

// src/components/DetailSidebar.jsx (Bottom of file)

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      {/* Changed text-neutral-400 to text-neutral-500/80 for better contrast on gray bg */}
      <div className="text-xs font-medium uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 truncate text-sm text-neutral-200">
        {value || "—"}
      </div>
    </div>
  );
}

function CopyBtn({ text }) {
  return (<button onClick={() => navigator.clipboard.writeText(text)} className="p-1 rounded hover:bg-white/5" title="Copy to clipboard"><Copy className="h-4 w-4 text-neutral-400 hover:text-white" /></button>);
}

function PasswordField({ password }) {
  const [visible, setVisible] = useState(false);
  return (
    <>
      <span className="text-neutral-100">{visible ? (password || "—") : "••••••••"}</span>
      <button onClick={() => setVisible((v) => !v)} className="p-1 rounded hover:bg-white/5" title={visible ? "Hide password" : "Show password"}>
        {visible ? <EyeOff className="h-4 w-4 text-neutral-400 hover:text-white" /> : <Eye className="h-4 w-4 text-neutral-400 hover:text-white" />}
      </button>
    </>
  );
}
function EnterpriseField({ label, value, icon, highlight = false }) {
  // Logic to handle empty values gracefully
  const displayValue = value && String(value).trim() !== "" && value !== "N/A" 
    ? value 
    : "—";

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
        {icon && <span className="opacity-70">{icon}</span>}
        <span>{label}</span>
      </div>
      <div 
        className={`truncate font-mono text-sm ${
          highlight ? "text-white" : "text-neutral-300"
        }`} 
        title={displayValue}
      >
        {displayValue}
      </div>
    </div>
  );
}


function ArchiveBadge({ state }) {
  const styleByTone = {
    available: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    dedup:     "border-sky-500/30 bg-sky-500/10 text-sky-300",
    pending:   "border-amber-500/30 bg-amber-500/10 text-amber-300",
    failed:    "border-red-500/30 bg-red-500/10 text-red-300",
    none:      "border-neutral-700 bg-neutral-800/40 text-neutral-500",
  };

  const tooltip = {
    available: "Full log archive is available for download",
    dedup:     "Archive deduplicated — identical content to a previous snapshot",
    pending:   "Archive is currently being uploaded",
    failed:    "Archive upload failed — retry in progress",
    none:      "No archive was stored for this incident",
  }[state.tone];

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${styleByTone[state.tone]}`}
      title={tooltip}
    >
      <HardDrive className="h-3 w-3" />
      <span>{state.label}</span>
    </span>
  );
}